import {
  type ApiKey,
  type ApiKeyInput,
  apiKeySchema,
  type ApiKeysList,
  apiKeysListSchema,
  type CreatedApiKey,
  createdApiKeySchema,
  type KeyClient,
  type KeyExpiry,
} from "@crux/contracts";
import { z } from "zod";
import { problem } from "./errors.js";

/**
 * The AI apps page's calls for its keys (DESIGN.md "AI apps").
 *
 * Like People, these routes exist only in the Rust API, and the typed client is
 * generated from Node's OpenAPI document; so these are plain requests whose
 * answers are parsed with the shared schemas in `@crux/contracts`.
 */

const refusalSchema = z.object({ message: z.string() });

async function send(
  workspaceId: string,
  path: string,
  init: { readonly method: string; readonly body?: unknown },
): Promise<Response> {
  return await fetch(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/api-keys${path}`,
    {
      method: init.method,
      credentials: "same-origin",
      headers: init.body === undefined ? {} : { "content-type": "application/json" },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    },
  );
}

/** A refusal in the server's own words when it gave some. */
async function refused(response: Response, attempt: string): Promise<Error> {
  if (response.status === 400 || response.status === 409) {
    const parsed = refusalSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) return new Error(parsed.data.message);
  }
  return new Error(problem(response.status, attempt));
}

export async function fetchApiKeys(workspaceId: string): Promise<ApiKeysList> {
  const response = await send(workspaceId, "", { method: "GET" });
  if (!response.ok) throw await refused(response, "load the API keys");
  return apiKeysListSchema.parse(await response.json());
}

export async function createApiKey(
  workspaceId: string,
  input: ApiKeyInput & { readonly expiresIn: KeyExpiry; readonly client: KeyClient },
): Promise<CreatedApiKey> {
  const response = await send(workspaceId, "", { method: "POST", body: input });
  if (response.status === 429) {
    throw new Error("Too many keys made at once. Wait fifteen minutes and try again.");
  }
  if (!response.ok) throw await refused(response, "create the key");
  return createdApiKeySchema.parse(await response.json());
}

export async function updateApiKey(
  workspaceId: string,
  keyId: string,
  input: ApiKeyInput,
): Promise<ApiKey> {
  const response = await send(workspaceId, `/${encodeURIComponent(keyId)}`, {
    method: "PATCH",
    body: input,
  });
  if (!response.ok) throw await refused(response, "save the key");
  return apiKeySchema.parse(await response.json());
}

export async function rollApiKey(
  workspaceId: string,
  keyId: string,
): Promise<CreatedApiKey> {
  const response = await send(workspaceId, `/${encodeURIComponent(keyId)}/roll`, {
    method: "POST",
  });
  if (!response.ok) throw await refused(response, "roll the key");
  return createdApiKeySchema.parse(await response.json());
}

export async function deleteApiKey(workspaceId: string, keyId: string): Promise<void> {
  const response = await send(workspaceId, `/${encodeURIComponent(keyId)}`, {
    method: "DELETE",
  });
  if (!response.ok) throw await refused(response, "delete the key");
}
